import React, { useMemo } from 'react'
import DashboardEditableRow from '@components/pages/Dashboard/DashboardEditableRow.component'
import DashboardMetricItem from '@components/pages/Dashboard/DashboardMetricItem.component'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import { pickMetricsByIds } from '@components/pages/Dashboard/Shared/DashboardMetricTileGrid.component'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import { metricDataToItemProps } from '@utils/dashboardMetrics.util'
import {
  hasNonZeroDashboardValues,
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'

const PERFORMANCE_UTIL_ROW1_IDS = [
  'provider-util',
  'hygiene-util',
  'new-patients',
]
const PERFORMANCE_UTIL_ROW2_IDS = [
  'diagnosed-tx',
  'case-acceptance',
  'same-day-tx',
  'pending-tx-value',
]

type UtilizationMetricsProps = {
  editMode?: boolean
  schedulingUtilizationMetrics: DashboardMetricItemData[]
  schedulingLoading: boolean
  /** Scheduling overview response has arrived. */
  schedulingHasPayload: boolean
  clinicalEfficiencyMetrics: DashboardMetricItemData[]
  clinicalEfficiencyLoading: boolean
  /** Clinical efficiency response has arrived. */
  clinicalEfficiencyHasPayload: boolean
  diagnosedTreatmentLoading: boolean
  /** Diagnosed treatment response has arrived. */
  diagnosedTreatmentHasPayload: boolean
  caseAcceptanceLoading: boolean
  /** Case acceptance response has arrived. */
  caseAcceptanceHasPayload: boolean
  utilizationRow2Metrics: DashboardMetricItemData[]
}

function UtilizationMetricRow({
  editMode,
  loading,
  metrics,
  pending,
  skeletonColumns,
}: {
  editMode: boolean
  loading: boolean
  metrics: DashboardMetricItemData[]
  pending: boolean
  skeletonColumns: 3 | 4
}): React.ReactElement {
  return (
    <DashboardEditableRow
      editMode={editMode}
      className="dashboard-finance-row dashboard-performance-metric-tiles-row"
    >
      {pending ? (
        <DashboardSectionCard
          loading
          skeleton={{
            metrics: 4,
            metricColumns: skeletonColumns,
          }}
          className="dashboard-performance-full-width"
        />
      ) : (
        metrics.map((metric) => (
          <DashboardSectionCard
            key={metric.id ?? metric.leftLabel}
            isEmpty={shouldShowDashboardSectionEmpty(
              loading,
              // Targets (valueOver / goal rightLabel) do not establish activity.
              hasNonZeroDashboardValues(metric.value),
            )}
          >
            <DashboardMetricItem {...metricDataToItemProps(metric)} />
          </DashboardSectionCard>
        ))
      )}
    </DashboardEditableRow>
  )
}

export function UtilizationMetrics({
  editMode = false,
  schedulingUtilizationMetrics,
  schedulingLoading,
  schedulingHasPayload,
  clinicalEfficiencyMetrics,
  clinicalEfficiencyLoading,
  clinicalEfficiencyHasPayload,
  diagnosedTreatmentLoading,
  diagnosedTreatmentHasPayload,
  caseAcceptanceLoading,
  caseAcceptanceHasPayload,
  utilizationRow2Metrics,
}: UtilizationMetricsProps): React.ReactElement {
  const utilizationMetricPool = useMemo((): DashboardMetricItemData[] => {
    const avgUtil = clinicalEfficiencyMetrics.find(
      (metric) => metric.id === 'avg-provider-util',
    )
    const scheduling = schedulingUtilizationMetrics.map((metric) => {
      if (
        metric.id === 'provider-util' &&
        metric.value === '—' &&
        avgUtil?.value != null &&
        avgUtil.value !== '—'
      ) {
        return { ...metric, value: avgUtil.value }
      }
      return metric
    })
    return [...scheduling, ...utilizationRow2Metrics]
  }, [
    schedulingUtilizationMetrics,
    utilizationRow2Metrics,
    clinicalEfficiencyMetrics,
  ])
  const utilizationRow1Metrics = useMemo(
    (): DashboardMetricItemData[] =>
      pickMetricsByIds(utilizationMetricPool, PERFORMANCE_UTIL_ROW1_IDS),
    [utilizationMetricPool],
  )
  const utilizationRow2DisplayMetrics = useMemo(
    (): DashboardMetricItemData[] =>
      pickMetricsByIds(utilizationMetricPool, PERFORMANCE_UTIL_ROW2_IDS),
    [utilizationMetricPool],
  )

  // Row 1: scheduling utilization (provider-util falls back to clinical
  // efficiency). Row 2: treatment metrics plus scheduling-derived tiles.
  const row1Loading = schedulingLoading || clinicalEfficiencyLoading
  const row1HasPayload = schedulingHasPayload || clinicalEfficiencyHasPayload
  const row2Loading =
    schedulingLoading || diagnosedTreatmentLoading || caseAcceptanceLoading
  const row2HasPayload =
    schedulingHasPayload ||
    diagnosedTreatmentHasPayload ||
    caseAcceptanceHasPayload

  const row1Pending = isDashboardSectionPending(row1Loading, row1HasPayload)
  const row2Pending = isDashboardSectionPending(row2Loading, row2HasPayload)

  return (
    <>
      <UtilizationMetricRow
        editMode={editMode}
        loading={row1Loading}
        metrics={utilizationRow1Metrics}
        pending={row1Pending}
        skeletonColumns={3}
      />
      <UtilizationMetricRow
        editMode={editMode}
        loading={row2Loading}
        metrics={utilizationRow2DisplayMetrics}
        pending={row2Pending}
        skeletonColumns={4}
      />
    </>
  )
}
